import { Button } from '@/components/button';
import { StoryblokImage } from '@/components/customFields/StoryblokImage';
import ModalBox from '@/components/modalBox/ModalBox';
import { RichText as RichTextComponent } from '@/components/richText/RichText';
import { SvgIcon } from '@/components/svgIcon';
import VideoPlayer from '@/components/video/VideoPlayer';
import { getAssetTypeByUrl, getAssetUrl } from '@/helper/asset';
import { shareLink } from '@/helper/share';
import { useClipboard } from '@/hooks/useClipboard';
import FavoriteBorderOutlinedIcon from '@/public/icons/material/favorite-border-outlined.svg';
import Favorite from '@/public/icons/material/favorite.svg';
import IosShareIcon from '@/public/icons/material/ios-share.svg';
import VideoPLayIcon from '@/public/icons/play.svg';
import type {
  AssetStoryblok,
  ComponentVotingGalleryStoryblok,
  PartVotingGalleryItemStoryblok,
} from '@/types/component-types-sb';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import classes from './VotingItem.module.scss';

type VotingItemProps = {
  item: PartVotingGalleryItemStoryblok;
  blok: ComponentVotingGalleryStoryblok;
  hasVoted: boolean;
  votedFor: string[];
  setVotedFor: React.Dispatch<React.SetStateAction<string[]>>;
  voteCount?: number;
};

const VotingItem: React.FC<VotingItemProps> = ({
  item,
  blok,
  hasVoted = false,
  votedFor = [],
  setVotedFor,
  voteCount,
}) => {
  const { t } = useTranslation();
  const { copied, copyToClipboard } = useClipboard(null);
  const [canShare, setCanShare] = useState(false);
  const [isVideoModalOpen, setVideoModalOpen] = useState(false);

  // Toggle selected images for voting
  function vote(itemId: string) {
    setVotedFor((state) => {
      const arr: string[] = state.includes(itemId)
        ? state.filter((i) => i !== itemId) // remove id
        : [...state, itemId]; // add id
      return arr;
    });
  }

  // Checks if an item can be voted for
  function canVote(itemId: string): boolean {
    return votedFor.length < Number(blok.maxVotes) || votedFor.includes(itemId);
  }

  // Check if the current browser supports a share dialog
  useEffect(() => {
    setCanShare(typeof navigator.share !== 'undefined');
  }, []);

  const displayImage: AssetStoryblok | undefined = item.image;
  const itemId = item._uid;
  const aspectRatio = 1.5;

  // Determine if the video field points to a real video (not an image)
  const maybeVideo = item.video;
  const videoUrl = typeof maybeVideo === 'object' ? maybeVideo?.url || maybeVideo?.cached_url : undefined;
  const isVideoLinked = getAssetTypeByUrl(String(videoUrl)) === 'video';

  return (
    <div key={itemId} className={classes.item} id={`item-${itemId}`} data-testid={`voting-item-${itemId}`}>
      <figure className={classNames(classes.image)}>
        {displayImage ? (
          <div className={classes.imageWrapper} data-testid="voting-item-image-wrapper">
            <StoryblokImage className={classes.img} image={displayImage} aspectRatio={aspectRatio} />
            {isVideoLinked && (
              <button
                type="button"
                className={classes.playButton}
                aria-label={t('Video abspielen')}
                onClick={() => setVideoModalOpen(true)}
              >
                <SvgIcon inheritViewBox={true} aria-hidden="true" component={VideoPLayIcon}></SvgIcon>
              </button>
            )}
          </div>
        ) : null}
      </figure>

      {/* CTA under the video (full width), shown only when a real video is linked */}
      {isVideoLinked && (
        <Button
          data-testid="voting-item-video-cta"
          className={classNames(classes.actionButton, classes.videoCta)}
          variant="ctaFull"
          onClick={() => setVideoModalOpen(true)}
        >
          {t('Video abspielen')}
        </Button>
      )}

      {/* Headline and text under the image */}
      {(item.title || item.text) && (
        <div className={classes.content}>
          {item.title && <h6 className={classes.title}>{item.title}</h6>}
          {item.text && <RichTextComponent text={item.text} className={classNames(classes.text, 'font-copy-small')} />}
        </div>
      )}

      {/* Video Modal */}
      {isVideoLinked && (
        <ModalBox open={isVideoModalOpen} large={false} toggleOpen={() => setVideoModalOpen((o) => !o)}>
          <VideoPlayer
            url={String(videoUrl)}
            poster={getAssetUrl(displayImage) || undefined}
            ariaLabel={`Video${item.title ? `: ${item.title}` : ''}`}
          />
        </ModalBox>
      )}

      <div className={classes.actions}>
        <div className={classes.buttonsWrapper}>
          {canShare ? (
            <Button
              data-testid="voting-item-share-button"
              className={classes.actionButton}
              onClick={async () =>
                await shareLink({
                  url: `${window.location.origin}${window.location.pathname}#item-${itemId.toString()}`,
                  title: blok.shareTitle ?? '',
                  text: blok.shareText ?? '',
                })
              }
              variant="ctaFull"
              endIcon={<IosShareIcon />}
            >
              {t('Teilen')}
            </Button>
          ) : (
            <div className={classes.copyWrapper}>
              <Button
                data-testid="voting-item-copy-button"
                className={classes.actionButton}
                variant="ctaFull"
                endIcon={copied ? undefined : <IosShareIcon />}
                onClick={() =>
                  copyToClipboard(`${window.location.origin}${window.location.pathname}#item-${itemId.toString()}`)
                }
              >
                {copied ? t('Link kopiert') : t('Teilen')}
              </Button>
            </div>
          )}
          {!hasVoted && (
            <Button
              data-testid="voting-item-vote-button"
              disabled={!canVote(itemId.toString())}
              className={classNames(classes.actionButton, classes.like)}
              variant="ctaFull"
              endIcon={
                votedFor.includes(itemId.toString()) ? (
                  <Favorite color="error" className={classes.likedIcon} />
                ) : (
                  <FavoriteBorderOutlinedIcon />
                )
              }
              onClick={() => vote(itemId.toString())}
              aria-pressed={votedFor.includes(itemId.toString())}
            >
              {blok.voteForLabel}
            </Button>
          )}
        </div>
        <div className={classes.badgeWrapper}>
          {typeof voteCount !== 'undefined' && (
            <span
              data-testid="voting-item-vote-count"
              className={classNames(classes.voteCount, classes.voteCountBadge)}
              aria-label={`Abgestimmt ${voteCount?.toLocaleString('de-DE')} Stimmen`}
            >
              <Favorite aria-hidden="true" />
              <span aria-hidden="true" className="font-action-label-small">
                {voteCount?.toLocaleString('de-DE')}
              </span>
            </span>
          )}
          {hasVoted && votedFor.includes(itemId.toString()) && (
            <span
              data-testid="voting-item-voted-label"
              className={classNames(classes.votedLabel, 'font-action-label-small')}
            >
              {t('Abgestimmt')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default VotingItem;
